import { MutableRefObject } from 'react';

import { CreoConnector, MTLData, MTLMessage, mockData } from '../connector/CreoConnector';
import { InboundPartnr, PartNrMessage, Material, SurfaceTreatment, AssignmentMessage, MaterialColor, MaterialDimension, Assignment, AlternateMaterial } from '../types/CommonTypes';
import { buildAssignment, buildMultipleAssignments } from '../utils/buildAssignment';
import { ConditionType } from './usePartNrAssignment';
import { usePostMessageConnector } from './usePostMessageConnector';
import { useAssignment } from './useAssignment';
import Config from '../utils/config';

type creoConnector = {
    connector: MutableRefObject<CreoConnector | null>;
    sendMTL: (data?: MTLData) => void;
    sendAssignment: (partNrs: InboundPartnr[], material?: Material, surfaceTreatment?: SurfaceTreatment, condition?: MaterialColor | MaterialDimension | null, conditionType?: ConditionType | null, alternates?: AlternateMaterial[]) => void;
};

export function useCreoConnector(): creoConnector{
    const assignment = useAssignment();
    const connector = usePostMessageConnector({
        factory: () => new CreoConnector(),
        initOptions: {
            targetOrigin: Config.get('toCreoOrigin'),
            allowedOrigins: Config.get('fromCreoOrigins'),
        },
        requiredMessageTypes: [
            { type: PartNrMessage.type, version: PartNrMessage.version },
            { type: MTLMessage.type, version: MTLMessage.version },
        ],
        onHandshakeComplete: (c) => {
            c.onMessage(PartNrMessage.type, (msg) => {
                const m = msg as PartNrMessage;
                assignment.setRawPartNrs(m.partNrs);
                const preloadedAlternates: AlternateMaterial[] = m.partNrs.flatMap(p =>
                    (p.alternates ?? []).map(a => ({
                        id: crypto.randomUUID(),
                        partNr: p.partnr,
                        materialId: a.bmId,
                        materialName: a.bmName ?? a.bmId,
                        materialDensity: a.bmDensity,
                        conditionType: a.conditionType ?? null,
                        condition: a.condition ?? null,
                    }))
                );
                if (preloadedAlternates.length > 0) {
                    assignment.initAlternates(preloadedAlternates);
                }
            });
        },
    });

    function creoFormat(assign: Assignment, material?: Material, surfaceTreatment?: SurfaceTreatment): void {
            // Creo-specific name formatting:
            // bmName → {adb}_{name}_{colorLabel|dimensionValue}
            // stName → {adb}_{stName}        
            if (assign.bmName) {
                const adbPrefix = material?.adb ? `${material.adb}_` : '';
                const condition = assignment.assignedCondition;
                const conditionSuffix =
                    assignment.assignedConditionType === 'color'
                        ? (condition as MaterialColor)?.colorLabel
                        : assignment.assignedConditionType === 'dimension'
                        ? (condition as MaterialDimension)?.dimensionValue
                        : undefined;
                const suffix = conditionSuffix ? `_${conditionSuffix}` : '';
                assign.bmName = `${adbPrefix}${assign.bmName}${suffix}`;
            }
            
            if (assign.stName) {
                const stAdbPrefix = surfaceTreatment?.adb ? `${surfaceTreatment.adb}_` : '';
                assign.stName = `${stAdbPrefix}${assign.stName}`;
            }
    }

    function sendMTL(data: MTLData | undefined = undefined, alternates?: AlternateMaterial[]): void {
        let msg = new MTLMessage(data);
        let material = assignment.assignedMaterial || assignment.pendingMaterial;
        let surfaceTreatment = assignment.assignedTreatment || assignment.pendingTreatment;
        let assignments = buildMultipleAssignments(assignment.viewPartNrs, material, surfaceTreatment, assignment.assignedCondition, assignment.assignedConditionType);
        assignments.forEach((assign) => creoFormat(assign, material, surfaceTreatment));
        msg.assignment = assignments;
        if (alternates !== undefined) {
            msg.alternates = alternates;
        }
        connector.current?.send(msg);
    }

    function sendAssignment(_partNrs: InboundPartnr[], _material?: Material, _surfaceTreatment?: SurfaceTreatment, _condition?: MaterialColor | MaterialDimension | null, _conditionType?: ConditionType | null, _alternates?: AlternateMaterial[]): void {
        //In Creo we send all data in the sendMTL function
    }

    return { connector, sendMTL, sendAssignment };
}